import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { editProject, getProjectByID, getWhatsAppFlows } from '../../services/api/implementation/impl';
import { isFlowSelectableForInvoiceNotification } from '../../utils/whatsappFlowUtils';

function formatFlowOptionLabel(flow) {
  const name = flow?.name || 'Untitled flow';
  const slug = flow?.slug ? ` (${flow.slug})` : '';
  return `${name}${slug}`;
}

function parseFlowId(value) {
  return value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
}

function WhatsAppFlowConfigSection({
  title,
  description,
  enabledLabel,
  flowLabel,
  emptyFlowsMessage,
  enabled,
  onToggleEnabled,
  selectedFlowId,
  onSelectFlowId,
  linkedFlow,
  flows,
  isFlowsLoading,
  isFlowsError,
  onRetryFlows,
  isSaving
}) {
  const selectableFlows = useMemo(
    () => flows.filter(isFlowSelectableForInvoiceNotification),
    [flows]
  );

  const flowOptionsForSelect = useMemo(() => {
    const byId = new Map();
    selectableFlows.forEach((flow) => byId.set(Number(flow.id), flow));
    if (linkedFlow?.id != null && !byId.has(Number(linkedFlow.id))) {
      byId.set(Number(linkedFlow.id), linkedFlow);
    }
    if (selectedFlowId != null && !byId.has(Number(selectedFlowId))) {
      byId.set(Number(selectedFlowId), {
        id: selectedFlowId,
        name: linkedFlow?.name || `Flow #${selectedFlowId}`,
        slug: linkedFlow?.slug
      });
    }
    return Array.from(byId.values());
  }, [selectableFlows, linkedFlow, selectedFlowId]);

  return (
    <section className="space-y-4 rounded-xl border border-stone-200 bg-stone-50/50 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggleEnabled(e.target.checked)}
          disabled={isSaving}
          className="h-4 w-4 rounded border-gray-300 text-customBlue focus:ring-customBlue"
        />
        {enabledLabel}
      </label>

      {enabled ? (
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-gray-600">{flowLabel}</label>
          {isFlowsLoading ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading flows…
            </div>
          ) : isFlowsError ? (
            <div className="flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
              <p>Could not load WhatsApp flows.</p>
              <button
                type="button"
                onClick={onRetryFlows}
                className="w-fit rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
                Try again
              </button>
            </div>
          ) : (
            <>
              <select
                value={selectedFlowId ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onSelectFlowId(value === '' ? null : Number(value));
                }}
                disabled={isSaving}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-customBlue focus:ring-1 focus:ring-customBlue disabled:opacity-50">
                <option value="">Select a flow…</option>
                {flowOptionsForSelect.map((flow) => (
                  <option key={flow.id} value={flow.id}>
                    {formatFlowOptionLabel(flow)}
                  </option>
                ))}
              </select>
              {selectableFlows.length === 0 ? (
                <p className="mt-2 text-xs text-gray-500">{emptyFlowsMessage}</p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

const InvoiceWhatsAppSettingsModal = ({ projectId, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const numericProjectId = Number(projectId);

  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectByID(projectId),
    enabled: Boolean(isOpen && projectId)
  });

  const savedInvoiceEnabled = Boolean(project?.invoice_whatsapp_enabled);
  const savedInvoiceFlowId = parseFlowId(project?.invoice_whatsapp_flow_id);
  const savedPaymentEnabled = Boolean(project?.payment_whatsapp_enabled);
  const savedPaymentFlowId = parseFlowId(project?.payment_whatsapp_flow_id);

  const [invoiceEnabled, setInvoiceEnabled] = useState(savedInvoiceEnabled);
  const [invoiceFlowId, setInvoiceFlowId] = useState(savedInvoiceFlowId);
  const [paymentEnabled, setPaymentEnabled] = useState(savedPaymentEnabled);
  const [paymentFlowId, setPaymentFlowId] = useState(savedPaymentFlowId);

  useEffect(() => {
    if (!isOpen) return;
    setInvoiceEnabled(Boolean(project?.invoice_whatsapp_enabled));
    setInvoiceFlowId(parseFlowId(project?.invoice_whatsapp_flow_id));
    setPaymentEnabled(Boolean(project?.payment_whatsapp_enabled));
    setPaymentFlowId(parseFlowId(project?.payment_whatsapp_flow_id));
  }, [
    isOpen,
    project?.invoice_whatsapp_enabled,
    project?.invoice_whatsapp_flow_id,
    project?.payment_whatsapp_enabled,
    project?.payment_whatsapp_flow_id
  ]);

  const invoiceDirty =
    invoiceEnabled !== savedInvoiceEnabled ||
    (invoiceEnabled &&
      ((invoiceFlowId == null && savedInvoiceFlowId != null) ||
        (invoiceFlowId != null && savedInvoiceFlowId == null) ||
        (invoiceFlowId != null &&
          savedInvoiceFlowId != null &&
          Number(invoiceFlowId) !== Number(savedInvoiceFlowId)))) ||
    (!invoiceEnabled && savedInvoiceFlowId != null);

  const paymentDirty =
    paymentEnabled !== savedPaymentEnabled ||
    (paymentEnabled &&
      ((paymentFlowId == null && savedPaymentFlowId != null) ||
        (paymentFlowId != null && savedPaymentFlowId == null) ||
        (paymentFlowId != null &&
          savedPaymentFlowId != null &&
          Number(paymentFlowId) !== Number(savedPaymentFlowId)))) ||
    (!paymentEnabled && savedPaymentFlowId != null);

  const isDirty = invoiceDirty || paymentDirty;

  const {
    data: flows = [],
    isLoading: isFlowsLoading,
    isError: isFlowsError,
    refetch: refetchFlows
  } = useQuery({
    queryKey: ['whatsappFlows', numericProjectId],
    queryFn: () => getWhatsAppFlows(numericProjectId),
    enabled: Boolean(isOpen && projectId && (invoiceEnabled || paymentEnabled))
  });

  const { mutate: saveSettings, isPending: isSaving } = useMutation({
    mutationFn: (payload) => editProject(projectId, payload),
    onSuccess: () => {
      toast.success('WhatsApp settings saved');
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      onClose?.();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save settings');
    }
  });

  const handleSave = () => {
    if (!isDirty || isSaving) return;

    if (invoiceEnabled && invoiceFlowId == null) {
      toast.error('Select an invoice notification flow.');
      return;
    }

    if (paymentEnabled && paymentFlowId == null) {
      toast.error('Select a payment notification flow.');
      return;
    }

    saveSettings({
      invoice_whatsapp_enabled: invoiceEnabled,
      invoice_whatsapp_flow_id: invoiceEnabled ? Number(invoiceFlowId) : null,
      payment_whatsapp_enabled: paymentEnabled,
      payment_whatsapp_flow_id: paymentEnabled ? Number(paymentFlowId) : null
    });
  };

  const handleToggleInvoiceEnabled = (checked) => {
    setInvoiceEnabled(checked);
    if (!checked) {
      setInvoiceFlowId(null);
    } else if (savedInvoiceFlowId != null) {
      setInvoiceFlowId(savedInvoiceFlowId);
    }
  };

  const handleTogglePaymentEnabled = (checked) => {
    setPaymentEnabled(checked);
    if (!checked) {
      setPaymentFlowId(null);
    } else if (savedPaymentFlowId != null) {
      setPaymentFlowId(savedPaymentFlowId);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      width={50}
      name="WhatsApp notification settings"
      confirmOnClose
      isDirty={isDirty}
      onClose={onClose}>
      <div className="space-y-4">
        {isProjectLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading settings…
          </div>
        ) : (
          <>
            <WhatsAppFlowConfigSection
              title="Invoice WhatsApp Flow"
              description="Send a WhatsApp flow when invoice-related events occur for this project. Only approved Accounts flows are available."
              enabledLabel="Enable invoice WhatsApp notifications"
              flowLabel="Invoice notification flow"
              emptyFlowsMessage="No approved Accounts flows for this project."
              enabled={invoiceEnabled}
              onToggleEnabled={handleToggleInvoiceEnabled}
              selectedFlowId={invoiceFlowId}
              onSelectFlowId={setInvoiceFlowId}
              linkedFlow={project?.invoice_whatsapp_flow ?? null}
              flows={flows}
              isFlowsLoading={isFlowsLoading}
              isFlowsError={isFlowsError}
              onRetryFlows={refetchFlows}
              isSaving={isSaving}
            />

            <WhatsAppFlowConfigSection
              title="Payment WhatsApp Flow"
              description="Send a WhatsApp flow when a payment is recorded for this project. Only approved Accounts flows are available."
              enabledLabel="Enable payment WhatsApp notifications"
              flowLabel="Payment notification flow"
              emptyFlowsMessage="No approved Accounts flows for this project."
              enabled={paymentEnabled}
              onToggleEnabled={handleTogglePaymentEnabled}
              selectedFlowId={paymentFlowId}
              onSelectFlowId={setPaymentFlowId}
              linkedFlow={project?.payment_whatsapp_flow ?? null}
              flows={flows}
              isFlowsLoading={isFlowsLoading}
              isFlowsError={isFlowsError}
              onRetryFlows={refetchFlows}
              isSaving={isSaving}
            />

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
              <Button type="button" displayType="secondary" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || isSaving || isProjectLoading}
                isLoading={isSaving}>
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default InvoiceWhatsAppSettingsModal;
